import type { BoardHand, BoardHoldRole } from '../../types/device';

/**
 * A circuit: the holds of a climb read as the **order they are climbed in**
 * rather than as a set.
 *
 * One implementation for the two walls a circuit can be painted on — the board's
 * grid of LEDs and a photo of a wall — because none of this was ever about the
 * wall. What differs is only where a hold keeps its role: a board hold carries a
 * `role` field, a photo hold carries a **colour** (see photo-hold-roles). So
 * everything here takes a `roleOf` and neither surface has to restate the rules.
 *
 * Two roles are counted specially:
 *
 *   - a **foot** is stood on for the whole run rather than moved to, so it is no
 *     step of the circuit: it takes no number and the numbering passes over it;
 *   - a **match** is the opposite — one hold, two moves, because the climber
 *     reaches it with one hand and brings the other onto it — so it takes two of
 *     the circuit's numbers and two of its hands.
 *
 * The numbers are never stored. A hold's number is where it sits, which is why a
 * reorder renumbers everything by itself and why the order needs no column of
 * its own: the array's order *is* the circuit (see Boulder / BoardClimb).
 */

/** What the roles are called, wherever a circuit is painted. */
export type CircuitRole = BoardHoldRole;

/**
 * A hold of a circuit: whatever the surface stores, plus the hand it names and
 * whether the setter swapped that hand against the alternation.
 */
export interface CircuitHold {
  hand?: BoardHand | null;
  swap_hand?: boolean;
}

/** How a surface reads a hold's role back. */
export type RoleOf<T> = (hold: T) => CircuitRole;

/**
 * Where each role sits in the climbed order: a climb is climbed from its start
 * holds to its top, so those bracket everything between them. Kept in sync with
 * BoardClimb::HOLD_ROLE_ORDER and Boulder::HOLD_ROLE_ORDER.
 */
const ROLE_ORDER: Record<CircuitRole, number> = {
  start: 0,
  hand: 1,
  foot: 1,
  no_match: 1,
  match: 1,
  finish: 2,
};

/** How many moves of the circuit one hold is: a match is two, a foot none. */
export function stepCount(role: CircuitRole): number {
  if (role === 'foot') return 0;

  return role === 'match' ? 2 : 1;
}

/**
 * Start holds first, the top last, everything else exactly where the setter put
 * it.
 *
 * The sort is **stable**, so reordering the middle of a circuit still means
 * something while the two ends stay where a climb's ends belong — which is what
 * lets the setter drag freely instead of being refused.
 */
export function orderCircuit<T>(holds: T[], roleOf: RoleOf<T>): T[] {
  return [...holds]
    .map((hold, index) => ({ hold, index }))
    .sort((a, b) => ROLE_ORDER[roleOf(a.hold)] - ROLE_ORDER[roleOf(b.hold)] || a.index - b.index)
    .map(({ hold }) => hold);
}

/**
 * The run itself, as indexes into the selection: every hold but the feet, in the
 * order they are climbed.
 *
 * Indexes rather than the holds, because a place in the run is what everything
 * outside works in — the wall is told which hold to flash by its index in the
 * problem's holds (see Api::Gyms::BoardLightsController#blink_index), and a list
 * row points at one hold the same way.
 */
export function circuitSteps<T>(holds: T[], roleOf: RoleOf<T>): number[] {
  return holds.reduce<number[]>((steps, hold, index) => {
    if (roleOf(hold) !== 'foot') steps.push(index);
    return steps;
  }, []);
}

/** The other half of the selection: the feet, in the same terms. */
export function circuitFeet<T>(holds: T[], roleOf: RoleOf<T>): number[] {
  return holds.reduce<number[]>((feet, hold, index) => {
    if (roleOf(hold) === 'foot') feet.push(index);
    return feet;
  }, []);
}

/**
 * The number each hold wears, aligned with the holds themselves — null for a
 * foot, so the hold after a foot takes the next number and not the one after it.
 *
 * A match wears both of the numbers it takes ("3,4"): one hold, two moves, and
 * the hold after it carries on from the second of them.
 */
export function circuitNumbers<T>(holds: T[], roleOf: RoleOf<T>): (string | null)[] {
  let step = 0;

  return holds.map((hold) => {
    const moves = stepCount(roleOf(hold));
    if (moves === 0) return null;

    const first = ++step;
    if (moves === 1) return String(first);

    return `${first},${++step}`;
  });
}

/**
 * Where the hold this one would trade places with sits, or null when there is
 * none it may: off the end of the run, or one of the two ends of the climb,
 * which a hold climbed through must not take the place of.
 */
export function circuitNeighbour<T>(
  holds: T[],
  roleOf: RoleOf<T>,
  index: number,
  delta: -1 | 1
): number | null {
  const hold = holds[index];
  if (!hold || roleOf(hold) === 'foot') return null;

  const steps = circuitSteps(holds, roleOf);
  const at = steps.indexOf(index);
  const neighbour = steps[at + delta];
  if (at === -1 || neighbour === undefined) return null;

  return ROLE_ORDER[roleOf(holds[neighbour])] === ROLE_ORDER[roleOf(hold)] ? neighbour : null;
}

/** Whether this hold can take the place of the one before (or after) it. */
export function canMoveCircuitStep<T>(
  holds: T[],
  roleOf: RoleOf<T>,
  index: number,
  delta: -1 | 1
): boolean {
  return circuitNeighbour(holds, roleOf, index, delta) !== null;
}

/**
 * Swap this hold with the one before or after it in the run, leaving the feet
 * between them where they are — they are no step of it.
 *
 * Returns the holds unchanged where the move is not allowed, so the caller can
 * hand the result straight on.
 */
export function moveCircuitStep<T>(
  holds: T[],
  roleOf: RoleOf<T>,
  index: number,
  delta: -1 | 1
): T[] {
  const neighbour = circuitNeighbour(holds, roleOf, index, delta);
  if (neighbour === null) return holds;

  const moved = [...holds];
  [moved[index], moved[neighbour]] = [moved[neighbour], moved[index]];
  return moved;
}

/**
 * The selection with one hold dragged onto another's place — the list's own
 * gesture, where the button above moves one step at a time.
 *
 * Dropped wherever the setter let go and then settled: a climb is climbed from
 * its starts to its top, so the middle reorders freely and the two ends go back
 * where they belong instead of the drag being refused.
 */
export function dragCircuitHold<T>(holds: T[], roleOf: RoleOf<T>, from: number, to: number): T[] {
  if (!holds[from] || !holds[to] || from === to) return holds;

  const moved = [...holds];
  moved.splice(to, 0, ...moved.splice(from, 1));
  return orderCircuit(moved, roleOf);
}

/** Whether a single hand is named for this hold at all. */
function takesAHand(role: CircuitRole): boolean {
  return role !== 'start' && role !== 'foot';
}

/**
 * The first hold the setter's chosen hand belongs to: the first that is neither a
 * start nor a foot.
 *
 * A start is pulled on with **both** hands — that is what a start position is,
 * and a sit start marks the feet it is pushed off as well — so there is no one
 * hand to name there. The alternation begins on the first real move, which is
 * also the one hold the setter is asked about.
 */
export function circuitHandIndex<T>(holds: T[], roleOf: RoleOf<T>): number | null {
  const at = holds.findIndex((hold) => takesAHand(roleOf(hold)));

  return at === -1 ? null : at;
}

/**
 * Which hand takes each hold of a circuit that forces them, given the hand the
 * first real move is made with.
 *
 * The hands alternate down the run. Feet are skipped rather than counted — a
 * foot chip is not a hand, and counting one would flip the alternation for a hold
 * no hand touches — and so are the starts, which both hands are on. A **match**
 * spends two of the alternation's hands on one hold: the one stored is the hand
 * that gets there first, the other follows it on, and the hold after a match is
 * back on the hand the match began with.
 *
 * The setter may **swap** any later hold (`swap_hand`): it is then taken with
 * the other hand than the alternation would give it — the same hand twice in a
 * row, a bump — and the run alternates on from that. A swap is a fact about the
 * move rather than a hand of its own, so flipping a hold earlier in the run
 * flips everything after it, swaps included: the swapped hold stays a bump.
 * The first real move takes no swap — the hand it is made with is the starting
 * hand itself, which is what the setter flips there — so one left on it is a
 * leftover and is dropped.
 *
 * Derived rather than stored, and derived again on the way in (see
 * BoardClimb#assign_hands and Boulder#assign_photo_hands): the mode is a rule
 * over the order, and hands that disagreed with it would describe a climb nobody
 * could follow.
 */
export function assignCircuitHands<T extends CircuitHold>(
  holds: T[],
  roleOf: RoleOf<T>,
  startingHand: BoardHand
): T[] {
  let hand = startingHand;
  let first = true;

  return holds.map((hold) => {
    const role = roleOf(hold);
    if (!takesAHand(role)) return withoutHand(hold);

    const swapped = !first && !!hold.swap_hand;
    const taken = swapped ? otherCircuitHand(hand) : hand;
    const assigned = first ? withoutSwap({ ...hold, hand: taken }) : { ...hold, hand: taken };
    first = false;

    hand = stepCount(role) === 2 ? taken : otherCircuitHand(taken);
    return assigned;
  });
}

/**
 * Swap the hand this hold is taken with, and let the run alternate on from it.
 *
 * A second swap of the same hold takes the first back. The first real move is
 * not swapped but *started* on the other hand — see `circuitHandIndex` — and a
 * hold no hand is named for has nothing to swap, so both are left alone.
 */
export function swapCircuitHand<T extends CircuitHold>(
  holds: T[],
  roleOf: RoleOf<T>,
  index: number
): T[] {
  const hold = holds[index];
  if (!hold || !takesAHand(roleOf(hold)) || circuitHandIndex(holds, roleOf) === index) return holds;

  return holds.map((each, at) =>
    at === index ? (each.swap_hand ? withoutSwap(each) : { ...each, swap_hand: true }) : each
  );
}

/**
 * Strips the hands off a selection — and the swaps, which mean nothing without
 * them — for a climb that stops forcing them.
 */
export function clearCircuitHands<T extends CircuitHold>(holds: T[]): T[] {
  return holds.map(withoutHand);
}

function withoutHand<T extends CircuitHold>(hold: T): T {
  if (hold.hand === undefined && hold.swap_hand === undefined) return hold;

  const rest = { ...hold };
  delete rest.hand;
  delete rest.swap_hand;
  return rest;
}

function withoutSwap<T extends CircuitHold>(hold: T): T {
  if (hold.swap_hand === undefined) return hold;

  const rest = { ...hold };
  delete rest.swap_hand;
  return rest;
}

export function otherCircuitHand(hand: BoardHand): BoardHand {
  return hand === 'left' ? 'right' : 'left';
}

/**
 * Both hands a match is taken with, in the order they come onto the hold — one
 * entry for every other hold, and none where no hand is named.
 *
 * Nor where none can be: a hand left on a start or a foot is a leftover of a
 * repaint the alternation has not caught up with yet, not an answer, and the
 * pictures that paint the hands must not paint that (see `circuitStartingHand`,
 * which ignores it for the same reason).
 */
export function circuitHandsOf<T extends CircuitHold>(hold: T, roleOf: RoleOf<T>): BoardHand[] {
  const role = roleOf(hold);
  if (!hold.hand || !takesAHand(role)) return [];

  return stepCount(role) === 2 ? [hold.hand, otherCircuitHand(hold.hand)] : [hold.hand];
}

/**
 * The hand a circuit's first move is made with, read back off its holds — the
 * first one that names a hand, every save having rewritten the run from it.
 * Falls back to the left hand, as the server does.
 *
 * Only the holds a hand is actually named for are asked: a hand left on a start
 * is a leftover rather than an answer, and letting it seed the alternation would
 * flip a whole circuit over a hand nobody chose.
 */
export function circuitStartingHand<T extends CircuitHold>(
  holds: T[],
  roleOf: RoleOf<T>
): BoardHand {
  return holds.find((hold) => hold.hand && takesAHand(roleOf(hold)))?.hand ?? 'left';
}
